import React, { useEffect, useState } from 'react';
import { Alert, Button, Typography } from 'antd';
import { fetchPlanDoc, listPlanDocs } from '../illustration/api';

const { Title, Paragraph, Text } = Typography;

function downloadText(filename, text, type) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function PlansPage() {
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);
  const [srcDoc, setSrcDoc] = useState('');
  const [raw, setRaw] = useState('');
  const [loadingDoc, setLoadingDoc] = useState(false);

  useEffect(() => {
    document.title = 'Plans · Illustration Studio';
    let cancelled = false;
    listPlanDocs()
      .then((data) => {
        if (!cancelled) setGroups(data.groups || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Could not load plans');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openDoc = async (file) => {
    setActive(file);
    setLoadingDoc(true);
    setError(null);
    try {
      const html = await fetchPlanDoc(file.path, 'html');
      setSrcDoc(html);
      document.title = `${file.title || file.filename} · Plans`;
      if (file.path.endsWith('.md')) {
        setRaw(await fetchPlanDoc(file.path, 'raw'));
      } else {
        setRaw('');
      }
    } catch (e) {
      setSrcDoc('');
      setRaw('');
      setError(e.message || 'Could not load that file');
    } finally {
      setLoadingDoc(false);
    }
  };

  const closeDoc = () => {
    setActive(null);
    setSrcDoc('');
    setRaw('');
    document.title = 'Plans · Illustration Studio';
  };

  const downloadActive = () => {
    if (!active || !raw) return;
    downloadText(active.filename, raw, 'text/markdown');
  };

  const downloadFile = async (file) => {
    try {
      const text = await fetchPlanDoc(file.path, 'raw');
      downloadText(file.filename, text, 'text/markdown');
    } catch (e) {
      setError(e.message || 'Could not download that file');
    }
  };

  if (active) {
    return (
      <div className="workflow-shell__content plans-viewer">
        <div className="plans-viewer__bar">
          <nav className="plans-viewer__crumb" aria-label="Plans">
            <button type="button" className="plans-viewer__back" onClick={closeDoc}>
              Plans
            </button>
            <span className="plans-viewer__sep" aria-hidden="true">/</span>
            <span className="plans-viewer__title">{active.title || active.filename}</span>
          </nav>
          {raw ? (
            <Button size="small" onClick={downloadActive} disabled={loadingDoc}>
              Download .md
            </Button>
          ) : null}
        </div>
        {error ? <Alert type="error" showIcon title={error} style={{ margin: '0 var(--space-6) var(--space-3)' }} /> : null}
        <iframe
          className="plans-viewer__frame"
          title={active.title || active.filename}
          sandbox=""
          srcDoc={loadingDoc ? '' : srcDoc}
        />
      </div>
    );
  }

  return (
    <div className="workflow-shell__content">
      <Title
        level={1}
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title)',
          letterSpacing: 'var(--tracking-title)',
          marginBottom: 4,
        }}
      >
        Plans
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 'var(--space-6)' }}>
        Each plan with the spec that goes with it. View opens it across this page. Download saves the Markdown.
      </Paragraph>
      {error ? <Alert type="error" showIcon title={error} style={{ marginBottom: 12 }} /> : null}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {groups.map((group) => (
          <li key={group.slug}>
            <Text strong>{group.title}</Text>
            {group.date ? (
              <Text type="secondary" style={{ marginInlineStart: 8 }}>
                {group.date}
              </Text>
            ) : null}
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
              <DocRow label="Plan" file={group.plan} onOpen={openDoc} onDownload={downloadFile} />
              {group.spec ? (
                <DocRow label="Spec" file={group.spec} onOpen={openDoc} onDownload={downloadFile} />
              ) : null}
              {group.html.map((file) => (
                <DocRow key={file.path} label="HTML" file={file} onOpen={openDoc} />
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DocRow({ label, file, onOpen, onDownload }) {
  if (!file) return null;
  return (
    <li style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 6 }}>
      <Text type="secondary" style={{ minWidth: '3.5rem' }}>{label}</Text>
      <span>{file.filename}</span>
      <Button size="small" onClick={() => onOpen(file)}>View</Button>
      {onDownload && file.path.endsWith('.md') ? (
        <Button size="small" onClick={() => onDownload(file)}>Download .md</Button>
      ) : null}
    </li>
  );
}
